<template>
  <div class="sticky w-full h-14 bg-white z-50 shadow-md flex justify-between items-center top-0">
    <!-- Version mobile -->
    <div class="flex md:hidden justify-between items-center app-container">
      <Link href="/">
        <img class="block h-5 w-auto" :src="logoUrl" alt="Logo Archibien" />
      </Link>

      <!-- Mobile menu button (Open/Close navigation mobile) -->
      <button
        type="button"
        class="inline-flex items-center text-gray-500 justify-center p-2 rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue"
        aria-controls="mobile-menu"
        aria-expanded="false"
        @click="toggleMenu"
      >
        <span class="sr-only">Open main menu</span>
        <XIcon v-if="menuVisible" class="h-6 w-6" />
        <MenuIcon v-else class="h-6 w-6" />
      </button>
    </div>

    <!-- Navigation mobile -->
    <div
      v-if="menuVisible"
      ref="mobileMenuRef"
      class="absolute md:hidden top-0 left-0 w-full p-4 bg-white mt-14 shadow-md border-t border-gray-300"
    >
      <nav class="flex flex-col divide-y font-medium text-sm text-gray-500">
        <div class="flex flex-col space-y-5 mb-5 text-center">
          <component
            :is="item.inertia ? 'Link' : 'a'"
            v-for="item in menuItems"
            :key="item.path"
            :href="item.path"
            class="flex items-center space-x-2 mx-auto"
            :class="{ 'text-blue-500 ': isUrlActive(item.path) }"
            @click.stop="menuVisible = false"
          >
            <component :is="item.icon" class="h-4 w-4 stroke-1-2" />
            <span>{{ item.label }}</span>
          </component>
        </div>
        <div class="pt-5 flex flex-col space-y-5 text-center">
          <div class="flex items-center space-x-1 text-sm mx-auto">
            <avatar :user="user" size="small" />
            <span>{{ userName }}</span>
          </div>
          <component
            :is="item.inertia ? 'Link' : 'a'"
            v-for="item in secondaryItems"
            :key="item.path"
            :href="item.path"
            class="flex items-center space-x-2 mx-auto"
            :class="{ 'text-blue-500': isUrlActive(item.path) }"
            @click.stop="menuVisible = false"
          >
            <component :is="item.icon" class="h-4 w-4 stroke-2" />
            <span>{{ item.label }}</span>
          </component>
          <component
            :is="item.inertia ? 'Link' : 'a'"
            v-for="item in accountItems"
            :key="item.path"
            :href="item.path"
            class="flex items-center space-x-2 mx-auto"
            :class="{ 'text-blue-500 ': isUrlActive(item.path) }"
            @click.stop="menuVisible = false"
          >
            <component :is="item.icon" class="h-4 w-4 stroke-2" />
            <span>{{ item.label }}</span>
          </component>
          <form action="/logout/" method="POST">
            <CsrfToken />
            <button type="submit" class="flex items-center space-x-2 mx-auto text-red-500">
              <LogoutIcon class="h-4 w-4 stroke-2" />
              <span>Déconnexion</span>
            </button>
          </form>
          <button
            v-if="$root.hasRole('manager')"
            class="flex items-center space-x-2 mx-auto"
            @click="openFeedbackModal"
          >
            <SolidChatAlt2Icon class="h-4 w-4" />
            <span>Feedback</span>
          </button>
        </div>
      </nav>
    </div>

    <!-- Version desktop -->
    <div class="hidden md:flex justify-between items-center app-container h-full">
      <Link href="/" class="cursor-pointer flex items-center">
        <img class="h-5 w-auto block" :src="logoUrl" alt="Logo Archibien" />
        <Badge v-if="appMode == 'development'" color="red" class="ml-2" icon="TerminalIcon"
          >Dev</Badge
        >
        <Badge v-else-if="appMode == 'review'" color="orange" class="ml-2" icon="BadgeCheckIcon"
          >Review</Badge
        >
      </Link>
      <nav class="h-full flex space-x-6 text-sm text-gray-600 items-center">
        <component
          :is="item.inertia ? 'Link' : 'a'"
          v-for="item in menuItems"
          :key="item.path"
          :href="item.path"
          class="h-full hover:text-blue-500 cursor-pointer flex items-center space-x-1 border-b-2 border-transparent hover:border-blue-500"
          :class="{ 'text-blue-500 border-blue-500': isUrlActive(item.path) }"
        >
          <component :is="item.icon" class="h-4 w-4 stroke-1" />
          <span class="hidden lg:block">{{ item.label }}</span>
        </component>
        <div
          class="h-full absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0 border-b-2 border-transparent hover:border-blue-500 hover:text-blue-500 cursor-pointer"
          @click="toggleDropDown"
        >
          <!-- Profile dropdown -->
          <div class="ml-3 relative hidden sm:block">
            <div>
              <button
                id="user-menu"
                type="button"
                class="bg-white rounded-full flex items-center space-x-1 text-sm focus:outline-none"
                aria-expanded="false"
                aria-haspopup="true"
              >
                <span>{{ userName }}</span>
                <div class="relative">
                  <avatar :user="user" />
                  <span
                    v-if="$root.notificationsCount"
                    class="absolute top-0 right-0 -translate-y-1 translate-x-1 block h-4 rounded-full ring-2 ring-white bg-red-600 text-white text-xs"
                    :class="{
                      'w-4': $root.notificationsCount.length <= 1,
                      'w-5': $root.notificationsCount.length === 2,
                      'w-6': $root.notificationsCount.length === 3,
                    }"
                  >
                    {{ $root.notificationsCount }}
                  </span>
                </div>
                <SolidChevronDownIcon class="h-4 w-4" />
              </button>
            </div>
            <transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <div
                v-if="dropDownVisible"
                :key="dropDownVisible"
                ref="dropdownRef"
                class="hidden sm:block origin-top-right divide-y divide-gray-200 absolute right-0 z-20 mt-4 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="user-menu"
              >
                <div v-if="secondaryItems.length > 0">
                  <component
                    :is="item.inertia ? 'Link' : 'a'"
                    v-for="item in secondaryItems"
                    :key="item.path"
                    :href="item.path"
                    class="px-4 py-3 text-sm flex items-center space-x-1 hover:bg-gray-100"
                    role="menuitem"
                    :class="isUrlActive(item.path) ? 'text-blue-500' : 'text-gray-600'"
                    @click.stop="dropDownVisible = false"
                  >
                    <component :is="item.icon" class="h-4 w-4 stroke-1" />
                    <span>{{ item.label }}</span>
                  </component>
                </div>
                <div>
                  <component
                    :is="item.inertia ? 'Link' : 'a'"
                    v-for="item in accountItems"
                    :key="item.path"
                    :href="item.path"
                    class="px-4 py-3 text-sm flex items-center space-x-1 hover:bg-gray-100"
                    :class="isUrlActive(item.path) ? 'text-blue-500' : 'text-gray-600'"
                    role="menuitem"
                    @click.stop="dropDownVisible = false"
                  >
                    <component :is="item.icon" class="h-4 w-4 stroke-1" />
                    <span>{{ item.label }}</span>
                    <badge
                      v-if="item.path == '/notifications'"
                      :count="$root.notificationsCount"
                      color="red"
                      flat
                      bg="600"
                    />
                  </component>
                  <form action="/logout/" method="POST">
                    <CsrfToken />
                    <button
                      type="submit"
                      class="w-full px-4 py-3 text-sm flex items-center space-x-1 hover:bg-gray-100 text-gray-600"
                      @click.stop="dropDownVisible = false"
                    >
                      <LogoutIcon class="h-4 w-4 stroke-1" />
                      <span>Déconnexion</span>
                    </button>
                  </form>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import { getMenuItems, getSecondaryItems, getAccountItems } from '@/api/navigation'
import Avatar from '@/components/users/Avatar.vue'
import Badge from '@/components/ui/Badge.vue'
import { onClickOutside } from '@vueuse/core'
import { usePage } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import CsrfToken from '@/components/CsrfToken.vue'
import logoUrl from '@/assets/logos/logo.svg?inline'

export default {
  name: 'Navbar',
  components: {
    Avatar,
    Badge,
    CsrfToken,
  },
  setup() {
    // build menu
    const user = computed(() => usePage().props.user)
    const userRole = user.value.role
    const menuItems = getMenuItems(userRole)
    const secondaryItems = getSecondaryItems(userRole)
    const accountItems = getAccountItems(userRole)
    const url = computed(() => usePage().url)

    const isUrlActive = (path) => {
      return url.value.startsWith(path.replace('owned/', '').replace('assigned/', ''))
    }
    const appMode = __MODE__

    // handle account menu/dropdown closing/opening
    const dropdownRef = ref(null)
    const dropDownVisible = ref(false)
    const menuVisible = ref(false)
    onClickOutside(dropdownRef, () => (dropDownVisible.value = false))
    const closeDropdown = () => {
      dropDownVisible.value = false
    }
    // handle mobile menu
    const mobileMenuRef = ref(null)
    onClickOutside(mobileMenuRef, () => (menuVisible.value = false))
    return {
      isUrlActive,
      user,
      dropdownRef,
      mobileMenuRef,
      menuVisible,
      dropDownVisible,
      closeDropdown,
      menuItems,
      secondaryItems,
      accountItems,
      url,
      logoUrl,
      appMode,
    }
  },
  computed: {
    userName() {
      return this.user.first_name + ' ' + this.user.last_name
    },
  },
  methods: {
    toggleDropDown() {
      this.dropDownVisible = !this.dropDownVisible
    },
    toggleMenu() {
      this.menuVisible = !this.menuVisible
    },
    openFeedbackModal() {
      this.menuVisible = false
      this.$root.openLocalModal('AddFeedback')
    },
  },
}
</script>
